// Shell do admin (`spec/17-frontend.md` §8.2). Mesmo `AppShell` do croqui que o seller,
// sem `RevenueCard` (`spec/24` §7) e sem `ModeToggle` (o admin filtra por tela, não
// globalmente, o `PageHeader` do kit já esconde o toggle na superfície admin).
//
// Ordem dos gates igual à do servidor (`apps/api/src/middlewares/auth-session.ts`):
// sessão → troca de senha → 2FA. As duas telas de gate vivem sob este layout, então
// ficam de fora do redirect, senão cada uma se redirecionaria para si mesma.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createFileRoute,
  Navigate,
  Outlet,
  useNavigate,
  useRouterState,
} from '@tanstack/react-router';
import { api } from '../api/client.ts';
import { adminBadges, adminCountersQueryOptions } from '../api/queries/shell.ts';
import { sessionQueryOptions } from '../api/session.ts';
import { adminNav } from '../app/nav-admin.ts';
import { NavDrawer } from '../components/app/nav-drawer.tsx';
import { CardSkeleton } from '../components/app/skeletons.tsx';
import { ProfileCard } from '../components/kit/profile-card.tsx';
import { Sidebar } from '../components/kit/sidebar.tsx';

const CHANGE_PASSWORD_PATH = '/admin/change-password';
const SETUP_2FA_PATH = '/admin/setup-2fa';

function AdminProfile({ name, email }: { name: string; email: string }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const signOut = useMutation({
    mutationFn: async () => api('/v1/me/auth/sign-out', { method: 'POST' }),
    onSettled: () => {
      queryClient.clear();
      void navigate({ to: '/login' });
    },
  });
  return (
    <ProfileCard
      name={name}
      email={email}
      onLogout={() => {
        signOut.mutate();
      }}
    />
  );
}

function AdminShell() {
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const session = useQuery(sessionQueryOptions);
  // `enabled`: TODA rota admin (`authSession`, surface admin) devolve
  // `password_change_required`/`mfa_required` até essas duas pendências fecharem, sem
  // este gate a query bateria a cada 30s nas telas `/admin/change-password` e
  // `/admin/setup-2fa` só pra levar 403 e re-navegar pra própria tela. Erro aqui não
  // quebra o shell (WEB-50B item 4): sem badge, sem toast.
  const counters = useQuery({
    ...adminCountersQueryOptions(),
    enabled:
      session.data?.role === 'admin' &&
      !session.data.mustChangePassword &&
      session.data.twoFactorEnabled,
  });
  const badges = counters.data === undefined ? undefined : adminBadges(counters.data);

  if (session.isPending) {
    return (
      <div className="min-h-dvh p-4 sm:p-8">
        <CardSkeleton />
      </div>
    );
  }
  if (session.data === null || session.data === undefined) {
    return <Navigate to="/login" search={{ next: pathname }} replace />;
  }
  if (session.data.role !== 'admin') {
    return <Navigate to="/no-access" replace />;
  }
  if (session.data.mustChangePassword && pathname !== CHANGE_PASSWORD_PATH) {
    return <Navigate to={CHANGE_PASSWORD_PATH} replace />;
  }
  if (
    !session.data.twoFactorEnabled &&
    pathname !== SETUP_2FA_PATH &&
    pathname !== CHANGE_PASSWORD_PATH
  ) {
    return <Navigate to={SETUP_2FA_PATH} replace />;
  }

  const user = session.data;

  return (
    <div className="flex min-h-dvh items-stretch">
      <Sidebar
        groups={adminNav}
        activePath={pathname}
        subtitle="Backoffice"
        {...(badges === undefined ? {} : { badges })}
        footer={<AdminProfile name={user.name} email={user.email} />}
        className="sticky top-0 hidden h-dvh lg:flex"
      />
      <main className="flex min-w-0 flex-1 flex-col gap-5 p-4 sm:py-8 sm:pr-8 sm:pl-6">
        <div className="flex items-center gap-3 lg:hidden">
          <NavDrawer
            groups={adminNav}
            activePath={pathname}
            subtitle="Backoffice"
            footer={<AdminProfile name={user.name} email={user.email} />}
          />
        </div>
        <Outlet />
      </main>
    </div>
  );
}

export const Route = createFileRoute('/admin')({ component: AdminShell });
